import dayjs, { Dayjs } from "dayjs";

import {
  IAdmitPatientType,
  IProfileDropdownEnums,
  IDoctorScheduleConsultationType,
  IDoctorScheduleOrderType,
  IKeyPairsValues,
  IPDashboardFilterType,
  IScheduleAppointmentFilterEnum,
  IUniversalSearchEnum,
  IMakeAppointmentEvent,
  IReceptionFilterEnum,
  IDropDownSelectionType,
} from "@/models/doctor";
import { ILanguageEnums, IPermission } from "@/models/onboarding";
import {
  IHealthAssesmentTypeEnums,
  IQualificationDetail,
} from "@/models/ApiRequestResponse/patient";
import {
  IAdmittedPatientDetail,
  IClinicAppointment,
  IClinicAppointmentSegment,
  ISlot,
  ISlotReserveFrom,
  IStartAndEndTime,
  IVideoAppointment,
  MakeBillAction,
  MakeBillActionType,
  MakeBillState,
} from "@/models/ApiRequestResponse/doctor";
import {
  IDashboardTabEnum,
  IOperatingSystemEnums,
  ISessionKeys,
} from "@/models";
import {
  IConsultationTypes,
  IInquiryTypeOptions,
  IOrderTypes,
  IWhoAreYouOptions,
} from "@/models/patient";
import { IModalKeyEnums } from "@/models/permission";
import { IPharmacyDiagnosticOrderFilterEnum } from "@/models/ApiRequestResponse/pharmacy";

import { durgapurCoordinates, mapUrl } from "@/utils/Config";

import { storage } from "@/utils/Storage";

import { ITabIndex, MESSAGE_TYPE } from "@/models/chat";
import {
  IConsultationStatus,
  IPatientSummaryOrIpAnalyticsAction,
  IPatientSummaryOrIpAnalyticsActionType,
  IPatientSummaryOrIpAnalyticsState,
  ITimePeriod,
  PatientsSummaryData,
} from "@/models/dashboard";

import { INotifyEnum } from "./toaster";
import { notify } from "./toaster";

const LIMIT = 40;

const PAGE_LIMIT = 10;

const pdfRegex = /^https?:\/\/.*\.pdf(\?.*)?$/;

const isClinic = () => {
  const clinicId = storage.getSessionToken("clinic_id");
  const doctorId = storage.getSessionToken("doctor_id");
  return clinicId && doctorId;
};

export const AllOrdersStatusList = [
  {
    label: "Upcoming",
    value: "PENDING",
  },
  {
    label: "Completed",
    value: "COMPLETED",
  },
  {
    label: "Cancelled",
    value: "CANCELLED",
  },
];

const enum IDELIVERY_TYPE {
  HOME_DELIVERY = "HOME_DELIVERY",
  PICKUP = "PICKUP",
}

const enum DIAGNOSTIC_TAB {
  DETAILS,
  RECORDS,
}

const convertDateToTimeStamp = (dateVal: Date | string) => {
  const selectDate = dayjs(dateVal);
  return selectDate.unix().toString();
};

const convertTimestampToISO = (timestamp: number) => {
  return dayjs.unix(timestamp).toISOString();
};

const convertTimestampToDDMMM = (
  timestamp: number,
  format: string = "DD MMM"
) => {
  return dayjs.unix(timestamp).format(format);
};

const defaultCalendarDates = {
  startDate: dayjs().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
  endDate: dayjs().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
};

const defaultCalendarDatesTimeStamp = {
  startDate: convertDateToTimeStamp(dayjs().startOf("day").toDate()),
  endDate: convertDateToTimeStamp(dayjs().endOf("day").toDate()),
};

const dayList = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const tranformIndexesToDays = (arr: string[]) => {
  let isComplete = false,
    result = [];
  for (let i = 0; i < arr.length; i++) {
    isComplete = +arr[i] - 1 === i;
    result.push(dayList[+arr[i] - 1]);
  }
  return { isComplete, list: result };
};
const tranformToDays = (arr: string[]) => {
  const result = [];
  for (let i = 0; i < arr.length; i++) {
    result.push(dayList[+arr[i] - 1]);
  }
  return result;
};
const tranformDaysToIndexes = (dayIndexesArr: string[]) => {
  return dayIndexesArr.map((dayIndex, index) =>
    dayList[+dayIndex - 1].toString()
  );
};

const convertDaysToRanges = (arr: string[]) => {
  const { isComplete, list } = tranformIndexesToDays(arr);

  if (isComplete) {
    if (list.length < 3) {
      if (list.length === 1) {
        return list[0];
      }
      return `${list[0]},${list[1]}`;
    } else {
      return `${list[0]}-${list[list.length - 1]}`;
    }
  } else {
    return `${list.join(",")}`;
  }
};

const timeInAmPmFormat = (value: string) => {
  return dayjs(`${dayjs().format("YYYY-MM-DD")}T${value}`).format("hh:mm a");
};

const transformClasses = function (...args: any) {
  return args.join(" ");
};

const languagesList = [
  {
    label: "English",
    value: ILanguageEnums.ENG,
  },
  {
    label: "हिंदी",
    value: ILanguageEnums.HIN,
  },
  {
    label: "ગુજરાતી",
    value: ILanguageEnums.GUJ,
  },
  {
    label: "বাংলা",
    value: ILanguageEnums.BEN,
  },
];

const doctorSpeakLanguages = (langList: string[]) => {
  if (langList.length === 0) return "";
  const langRes: string[] = [];
  languagesList.forEach((list, index) => {
    langList.forEach((lis, ind) => {
      if (list.value.includes(lis)) {
        langRes.push(list.label);
      }
    });
  });
  return langRes.join(" • ");
};

const doctorDegreeName = (qual: IQualificationDetail[]) => {
  return qual.map((list, index) => list.degree_name).join(",");
};

const getDateTime = (value: string) => {
  const now = dayjs(value);
  if (now.isSame(dayjs(), "day")) {
    return "Today";
  } else if (now.isSame(dayjs().add(1, "day"), "day")) {
    return "Tomorrow";
  } else {
    return now.format("MMM D, YYYY h:mm a");
  }
};

const getNextDates = (days = 45) => {
  const dates = [];
  const today = dayjs();
  for (let i = 0; i <= days; i++) {
    const nextDate = today.clone().add(i, "days");
    dates.push({
      date: nextDate.format("DD MMM"),
      day: i === 0 ? "Today" : nextDate.format("ddd"),
      fullDate: nextDate.format("YYYY MM DD"),
    });
  }
  return dates;
};

const findDoctorFilterListing = [
  {
    label: "Speciality",
    value: "speciality",
    isSelection: true,
  },
  {
    label: "In-Clinic",
    value: "In-Clinic",
    isSelection: false,
  },
  {
    label: "Video Call",
    value: "Video Call",
    isSelection: false,
  },
  {
    label: "Experince",
    value: "experience",
    isSelection: true,
  },
  {
    label: "Distance",
    value: "distance",
    isSelection: true,
  },
  {
    label: "Language",
    value: "language",
    isSelection: true,
  },
];

const openClinicMapLocation = (lat: number, lng: number) => {
  const url = `${mapUrl}${lat},${lng}`;
  window.open(url, "_blank");
};

const transformDateToMMDDYYY = (date: any, format = "MMM D, YYYY") => {
  return dayjs(date).format(format);
};

const base64ToFile = (base64String: string, fileName: string) => {
  // Split the base64 string into two parts: data type and data content
  const arr = base64String.split(",");
  // Extract the MIME type from the data type (the first part of the string)
  const mime = arr[0].match(/:(.*?);/)![1];
  // Decode the Base64 string to binary data
  const bstr = atob(arr[1]);
  // Create an array of bytes
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  // Create a new File object using the byte array and the MIME type
  return new File([u8arr], fileName, { type: mime });
};

const detectOS = () => {
  const userAgent = window.navigator.userAgent;
  if (/Windows|win/i.test(userAgent)) {
    return IOperatingSystemEnums.WINDOWS;
  } else if (/mac|Macintosh|MacIntel|MacPPC|Mac68K|Mac OS/i.test(userAgent)) {
    return IOperatingSystemEnums.MAC_OS;
  } else if (/linux|Linux/i.test(userAgent)) {
    return IOperatingSystemEnums.LINUX;
  } else if (/android/i.test(userAgent)) {
    return IOperatingSystemEnums.ANDROID;
  } else if (/iphone|ipad|ipod/i.test(userAgent)) {
    return IOperatingSystemEnums.IOS;
  } else {
    return IOperatingSystemEnums.UNKNOWN_OS;
  }
};

const convertDateYYYYMMDD = (date: string | Date | Dayjs) =>
  dayjs(date).format("YYYY-MM-DD");

const downloadReport = (
  documentURL: string,
  fileName: string,
  isDownloadAutomatically = true
) => {
  fetch(documentURL, {
    method: "GET",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.blob();
    })
    .then((blob) => {
      const fileUrl = documentURL.split("/");
      const fileExtension = fileUrl[fileUrl.length - 1].split(".");
      const url = URL.createObjectURL(blob);
      // Open in a new tab
      const newTab = window.open(url, "_blank");
      if (newTab) {
        newTab.onload = () => {
          setTimeout(() => {
            newTab.focus();
            newTab.print();
          }, 200);
        };
      }
      if (isDownloadAutomatically) {
        const downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.download = `${fileName}.${
          fileExtension[fileExtension.length - 1]
        }`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(downloadLink);
      }
    })
    .catch((error) =>
      console.error("There was a problem downloading the PDF:", error)
    );
};

const convertDateDDMMM = (date: string | Dayjs) => dayjs(date).format("DD MMM");

const topBarDropdownDispatchingEnums: any = {
  [IProfileDropdownEnums.PROFILE]: "isProfileOpen",
  [IProfileDropdownEnums.REVIEW]: "isReviewOpen",
  [IProfileDropdownEnums.AVAILABILITY_SETTINGS]: "isAvailabilityOpen",
  [IProfileDropdownEnums.CREATE_EDIT_AVAILABILITY]:
    "isCreateEditAvailabilityOpen",
  [IProfileDropdownEnums.CLINICAL_TEAM]: "isClinicalTeamOpen",
  [IProfileDropdownEnums.TEMPLATES]: "isTemplatesOpen",
  [IProfileDropdownEnums.CHANGE_HOSPITAL]: "isChangeHospitalOpen",
  [IProfileDropdownEnums.LOGOUT]: "isLogoutOpen",
  [IProfileDropdownEnums.NOTIFICATION]: "isNotificationOpen",
  [IProfileDropdownEnums.HELP_SUPPORT]: "isHelpSupportOpen",
  [IProfileDropdownEnums.MAKE_APPOINTMENT_FROM_NAVBAR]:
    "isMakeAppointmentFromNavBar",
  [IProfileDropdownEnums.MAKE_APPOINTMENT_FROM_CLINIC_PATIENTS]:
    "isMakeAppointmentFromClinicPatients",
};

const dayFilterListing = [
  {
    label: "Mon",
    value: "monday",
  },
  {
    label: "Tue",
    value: "tuesday",
  },
  {
    label: "Wed",
    value: "wednesday",
  },
  {
    label: "Thu",
    value: "thursday",
  },
  {
    label: "Fri",
    value: "friday",
  },
  {
    label: "Sat",
    value: "saturday",
  },
  {
    label: "Sun",
    value: "sunday",
  },
];

const reportsFilterList = [
  {
    label: "Dashboard",
    value: IDashboardTabEnum.DASHBOARD,
  },
  {
    label: "Schedule Reports",
    value: IDashboardTabEnum.SCHEDULE_REPORTS,
  },
  {
    label: "Appointment Reports",
    value: IDashboardTabEnum.APPOINTMENT_REPORTS,
  },
  {
    label: "Medical Partner",
    value: IDashboardTabEnum.MEDICAL_PARTNER,
  },
  {
    label: "Staff Reports",
    value: IDashboardTabEnum.STAFF_REPORTS,
  },
];

const scheduleFilterList = [
  {
    label: "All",
    value: IScheduleAppointmentFilterEnum.ALL,
  },
  {
    label: "Upcoming",
    value: IScheduleAppointmentFilterEnum.UPCOMING,
  },
  {
    label: "Completed",
    value: IScheduleAppointmentFilterEnum.COMPLETED,
  },
  {
    label: "Cancelled",
    value: IScheduleAppointmentFilterEnum.CANCELLED,
  },
];

const receptionFilterList = [
  {
    label: "All",
    value: IReceptionFilterEnum.ALL,
  },
  {
    label: "Paid",
    value: IReceptionFilterEnum.PAID,
  },
  {
    label: "Pending",
    value: IReceptionFilterEnum.PENDING,
  },
  {
    label: "Cancelled",
    value: IReceptionFilterEnum.CANCELLED,
  },
];

const pharmacyOrderFilterList = [
  {
    label: "All",
    value: IPharmacyDiagnosticOrderFilterEnum.ALL,
  },
  {
    label: "Home Delivery",
    value: IPharmacyDiagnosticOrderFilterEnum.HOME_DELIVERY,
  },
  {
    label: "Pickup",
    value: IPharmacyDiagnosticOrderFilterEnum.PICKUP,
  },
  {
    label: "Accepted",
    value: IPharmacyDiagnosticOrderFilterEnum.CONFIRMED,
  },
  {
    label: "Completed",
    value: IPharmacyDiagnosticOrderFilterEnum.COMPLETED,
  },
  {
    label: "Rejected",
    value: IPharmacyDiagnosticOrderFilterEnum.REJECTED,
  },
];

const diagnosticOrderFilterList = [
  {
    label: "All",
    value: IPharmacyDiagnosticOrderFilterEnum.ALL,
  },
  {
    label: "Accepted",
    value: IPharmacyDiagnosticOrderFilterEnum.CONFIRMED,
  },
  {
    label: "Uploaded",
    value: IPharmacyDiagnosticOrderFilterEnum.UPLOADED,
  },
  {
    label: "Completed",
    value: IPharmacyDiagnosticOrderFilterEnum.COMPLETED,
  },
  {
    label: "Rejected",
    value: IPharmacyDiagnosticOrderFilterEnum.REJECTED,
  },
];

const OrderTypeFilterList = [
  {
    label: "In-Clinic",
    value: IDoctorScheduleOrderType.IN_CLINIC,
  },
  {
    label: "Video Call",
    value: IDoctorScheduleOrderType.VIDEO_CALL,
  },
  {
    label: "Medicine",
    value: IDoctorScheduleOrderType.MEDICINE,
  },
  {
    label: "Test",
    value: IDoctorScheduleOrderType.TEST,
  },
];
const orderTypes: IOrderTypes = {
  [IDoctorScheduleOrderType.IN_CLINIC]: "In-Clinic",
  [IDoctorScheduleOrderType.VIDEO_CALL]: "Video Call",
  [IDoctorScheduleOrderType.MEDICINE]: "Medicine",
  [IDoctorScheduleOrderType.TEST]: "Test",
};

const languagesListInfo: IKeyPairsValues = {
  [ILanguageEnums.ENG]: "English",
  [ILanguageEnums.HIN]: "हिंदी",
  [ILanguageEnums.GUJ]: "ગુજરાતી",
  [ILanguageEnums.BEN]: "বাংলা",
};

const consultationTypes: IConsultationTypes = {
  [IDoctorScheduleConsultationType.IN_CLINIC]: "In-Clinic",
  [IDoctorScheduleConsultationType.VIDEO_CALL]: "Video Call",
};

const calculateDayFromTargetDate = (date: string, endDate?: string) => {
  const targetDate = dayjs(date);
  const today = endDate ? dayjs(endDate) : dayjs();
  const diffDays = today.diff(targetDate, "day");
  return diffDays === 0 && !endDate ? `Today` : `${diffDays}d`;
};

const transformDateToDDMMYYYY = (date: any) => {
  return dayjs(date).format("DD MMMM YYYY");
};

const ipDashboardListing = [
  {
    label: "ICU",
    value: IPDashboardFilterType.ICU,
    isSelection: true,
  },
  {
    label: "Ward",
    value: IPDashboardFilterType.WARD,
    isSelection: true,
  },
  {
    label: "Active",
    value: IPDashboardFilterType.ACTIVE,
    isSelection: false,
  },
  {
    label: "Discharge",
    value: IPDashboardFilterType.DISCHARGE,
    isSelection: false,
  },
];

const ipDashboardListingWeb = [
  {
    label: "Active",
    value: IPDashboardFilterType.ACTIVE,
  },
  {
    label: "Discharge",
    value: IPDashboardFilterType.DISCHARGE,
  },
];

const getTodayTomorrowDate = () => {
  const currentDate = dayjs();
  const nextDay = currentDate.add(1, "day");
  return {
    today: convertDateYYYYMMDD(currentDate),
    tomorrow: convertDateYYYYMMDD(nextDay),
  };
};

const admitPatientinitialState = {
  hospital_id: "",
  hospital_room_id: "",
  room_bed_id: "",
  sponsor_name: "",
  case_history: "",
  admission_date: null,
  hospital_uhid: "",
  name: "",
  member_id: "",
  age: "",
  gender: "",
  mobile_number: "",
  isAdmitPatientLoading: false,
};

const admitPatientDispatcher = (state: any, action: any) => {
  switch (action.type) {
    case IAdmitPatientType.SET_FIELD:
      return {
        ...state,
        [action.field]: action.value,
      };
    case IAdmitPatientType.SET_API_DATA:
      return {
        ...state,
        ...action.data,
      };
    default:
      return {};
  }
};

const getDay = (date: string) => {
  return dayjs(date).format("DD");
};

const getDateFormat = (date: string, format: string = "YYYY MM DD") => {
  return dayjs(date).format(format);
};

const formatTimestamp = (timestamp: number, format: string): string => {
  // Ensure timestamp is in milliseconds
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds

  // Map for date format replacements
  const map: { [key: string]: string } = {
    DD: String(date.getDate()).padStart(2, "0"), // Day with leading zero (e.g., 01, 23)
    D: String(date.getDate()), // Day without leading zero (e.g., 1, 23)
    MMM: date.toLocaleString("en-US", { month: "short" }), // Short month name (e.g., Jun)
    MMMM: date.toLocaleString("en-US", { month: "long" }), // Full month name (e.g., June)
    MM: String(date.getMonth() + 1).padStart(2, "0"), // Month as number with leading zero (e.g., 01, 12)
    M: String(date.getMonth() + 1), // Month as number without leading zero (e.g., 1, 12)
    YYYY: String(date.getFullYear()), // Full year (e.g., 2025)
    YY: String(date.getFullYear()).slice(-2), // Last two digits of year (e.g., 25)
  };

  // Replace format tokens with their corresponding values
  return format.replace(
    /DD|D|MMMM|MMM|MM|M|YYYY|YY/g,
    (matched) => map[matched] || matched
  );
};

const removeAttributesAdmitPatientDetail = (
  patientDetail: IAdmittedPatientDetail
) => {
  const {
    id,
    doctor_id,
    patient_id,
    discharged_date,
    status,
    created_at,
    updated_at,
    number,
    room_available,
    room_name,
    type,
    phone_number,
    countryCode,
    room_phone_number,
    hospital_name,
    hospital_image,
    bed_number,
    patient_details,
    ...rest
  } = patientDetail;
  return { ...rest, mobile_number: phone_number };
};

const getGreeting = () => {
  const currentHour = dayjs().hour();
  if (currentHour < 12) {
    return "Good morning";
  } else if (currentHour < 18) {
    return "Good afternoon";
  } else {
    return "Good evening";
  }
};

const doctorChipSearchList = [
  {
    label: "Appointments",
    value: IUniversalSearchEnum.APPOINTMENTS,
  },
  {
    label: "In Patients",
    value: IUniversalSearchEnum.IN_PATIENTS,
  },
  {
    label: "Clinical Team",
    value: IUniversalSearchEnum.CLINICAL_TEAM,
  },
  {
    label: "Reception",
    value: IUniversalSearchEnum.RECEPTION,
  },
];

const patientChipSearchList = [
  {
    label: "Doctors",
    value: IUniversalSearchEnum.FIND_DOCTORS,
  },
  {
    label: "Orders",
    value: IUniversalSearchEnum.ORDERS,
  },
  {
    label: "Diagnostic",
    value: IUniversalSearchEnum.DIAGNOSTIC,
  },
  {
    label: "Test",
    value: IUniversalSearchEnum.TEST,
  },
  {
    label: "Health Hub",
    value: IUniversalSearchEnum.HEALTH_HUB,
  },
];

const diagnosticChipSearchList = [
  {
    label: "Diagnostic",
    value: IUniversalSearchEnum.DIAGNOSTIC_CENTER,
  },
];

const universalSearchType: IKeyPairsValues = {
  [IUniversalSearchEnum.APPOINTMENTS]: "appointments",
  [IUniversalSearchEnum.IN_PATIENTS]: "patients",
  [IUniversalSearchEnum.CLINICAL_TEAM]: "clinical teams",
  [IUniversalSearchEnum.RECEPTION]: "reception",
  [IUniversalSearchEnum.PHARMACY_ORDER]: "pharmacy",
  [IUniversalSearchEnum.CLINIC_APPOINTMENTS]: "clinic",
  [IUniversalSearchEnum.DIAGNOSTIC_CENTER]: "diagnostic",
  // Patient search
  [IUniversalSearchEnum.FIND_DOCTORS]: "doctors",
  [IUniversalSearchEnum.ORDERS]: "orders",
  [IUniversalSearchEnum.DIAGNOSTIC]: "diagnostic",
  [IUniversalSearchEnum.TEST]: "tests",
  [IUniversalSearchEnum.HEALTH_HUB]: "health hub",
};

const timeInAmPmFromDateTime = (value: string | Dayjs) => {
  return dayjs(value).format("hh:mm a");
};

const convertDDMMYYYYHHMM = (value: string) => {
  return dayjs(value).format("DD MMMM YYYY, hh:mm A");
};
const randomCardColorGenerator = () => {
  const colorsList = [
    "var(--warm-neutral-50)",
    "#FFF1EF",
    "#E7F8FF",
    "#FFF8DF",
    "#E8FFF7",
    "#EFFFE0",
    "#FFE0F1",
    "#F9FFE0",
    "#E4FDFF",
  ];
  return colorsList[Math.floor(Math.random() * colorsList.length)];
};

const getHealthAssessmentImageByType: IKeyPairsValues = {
  [IHealthAssesmentTypeEnums.Brain]: "BrainImage",
  [IHealthAssesmentTypeEnums.Spine]: "SpineImage",
  [IHealthAssesmentTypeEnums.HEAD]: "BrainImage",
};

const getLocation = (isSkipDefault = false) => {
  const location: any =
    storage.getSessionToken("location") &&
    JSON.parse(storage.getSessionToken("location") as any);
  if (!isSkipDefault && !location) {
    const locationVal = {
      latitude: durgapurCoordinates.latitude,
      longitude: durgapurCoordinates.longitude,
    };
    return locationVal;
  }
  return location;
};

const getPermissionAccess = (
  moduleKey: IModalKeyEnums,
  featureKey: string,
  permissions: IPermission[]
) => {
  const module = permissions?.find((mod) => mod.moduleKey === moduleKey);
  if (!module) return null;
  const feature = module.features.find(
    (feat) => feat.featureKey === featureKey
  );
  return feature ? feature.permissionAccess : null;
};
const capitalizeFirstLetter = (value: string): string => {
  if (!value) return "";
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const roundToTwoDecimals = (value: number) => {
  if (typeof value === "number") {
    return Math.round(value * 100) / 100;
  }
  throw new Error("Input must be a number");
};

const toCamelCase = (str: string) => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

const convertKeysToCamelCase = (obj: any) => {
  const newObj: any = {};
  for (let key in obj) {
    const camelKey: any = toCamelCase(key); // Convert key to camelCase
    newObj[camelKey] =
      typeof obj[key] === "object" && !Array.isArray(obj[key])
        ? convertKeysToCamelCase(obj[key]) // Recursively handle nested objects
        : obj[key]; // Keep value as is
  }
  return newObj;
};

const getMedicalReportDocumentName = (url: string) => {
  return url?.split("/")?.pop();
};

const filteredNullEmptyArrayData = (data: any) =>
  Object.fromEntries(
    Object.entries(data).filter(
      ([_, value]) =>
        value !== null && !(Array.isArray(value) && value.length === 0)
    )
  );

const getTimeDifferenceInMinutes = (
  startTime: string,
  endTime: string
): number => {
  const start = dayjs(startTime, "HH:mm");
  const end = dayjs(endTime, "HH:mm");

  let diff = end.diff(start, "minute");
  if (diff < 0) {
    diff += 24 * 60;
  }
  return diff;
};

const calculateTotalDurationInMinutes = (
  startAndEndTime: IStartAndEndTime[]
): number =>
  startAndEndTime.reduce(
    (total, range) =>
      total + getTimeDifferenceInMinutes(range.start_time, range.end_time),
    0
  );

const calculateSlotDuration = (
  totalSlots: number,
  bufferBetweenSlots: number,
  startAndEndTime: IStartAndEndTime[]
): number => {
  let totalDuration = calculateTotalDurationInMinutes(startAndEndTime);
  totalDuration -= bufferBetweenSlots * (totalSlots - 1);
  return totalDuration / totalSlots;
};

const getFileNameFromUrl = (url: string) => {
  const regex = /\/([^\/]+)\.(pdf|jpeg|png)$/i;
  const match = url.match(regex);
  return match ? match[1] : null;
};
const truncateFileName = (fileName: string, maxLength: number = 40): string => {
  if (fileName.length <= maxLength) return fileName;
  return `${fileName.substring(0, maxLength - 3)}...`;
};

const clinicAppointmentSegregation = (
  clinicSlotWiseData: IClinicAppointment[]
) => {
  const result: ISlot[] = [];
  const slots = clinicSlotWiseData[0]?.slots;
  for (let i = 0; i < slots?.length; i++) {
    const slot = slots[i];
    const clinic = result.find(
      (c: any) =>
        c.clinicId === slot.clinicId &&
        c.consultationFee === slot.consultationFee
    );
    if (clinic) {
      clinic.slots.push(slot);
    } else {
      result.push({
        clinicId: slot.clinicId,
        clinicName: slot.clinicName,
        clinicCity: slot.clinicCity,
        clinicAddress: slot.clinicAddress,
        latitude: slot.latitude,
        longitude: slot.longitude,
        consultationFee: slot.consultationFee,
        slots: [slot],
      });
    }
  }
  if (clinicSlotWiseData.length === 0 && result.length === 0) return [];
  return [
    {
      date: clinicSlotWiseData[0]?.date,
      day: clinicSlotWiseData[0]?.day,
      slots: result,
    },
  ];
};

const getAppointmentSlotDetail = (
  selectedTab: IMakeAppointmentEvent,
  slotsDetail: {
    videoAppointment: IVideoAppointment[];
    clinicAppointment: IClinicAppointmentSegment[];
  },
  selectedAppointmentSlot: string,
  nextSixDays: { date: string; day: string; isAvailable: boolean }[],
  selectedDateTab: number
) => {
  if (selectedTab === IMakeAppointmentEvent.VIDEO_CALL) {
    for (const segment of slotsDetail["videoAppointment"]) {
      const slot = segment.slots.find(
        (slot: any) => slot.slotId === selectedAppointmentSlot
      );
      if (slot) {
        return {
          startTime: timeInAmPmFormat(slot.slotStartTime),
          endTime: timeInAmPmFormat(slot.slotEndTime),
          date: nextSixDays[selectedDateTab]?.date ?? "",
          consultationFee: slot.consultationFee,
        };
      }
    }
  } else {
    for (const segment of slotsDetail["clinicAppointment"]) {
      for (const segmentSlot of segment.slots) {
        const slot = segmentSlot.slots.find(
          (slot: any) => slot.slotId === selectedAppointmentSlot
        );
        if (slot) {
          return {
            startTime: timeInAmPmFormat(slot.slotStartTime),
            endTime: timeInAmPmFormat(slot.slotEndTime),
            date: nextSixDays[selectedDateTab]?.date ?? "",
            clinicId: segmentSlot.clinicId,
            clinicName: segmentSlot.clinicName,
            clinicCity: segmentSlot.clinicCity,
            clinicAddress: segmentSlot.clinicAddress,
            consultationFee: segmentSlot.consultationFee,
          };
        }
      }
    }
  }
};

const generateDeviceId = () => {
  let deviceId = storage.getSessionToken(ISessionKeys.DEVICE_ID);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    storage.setSessionToken(ISessionKeys.DEVICE_ID, deviceId);
  }
  return deviceId;
};

const getLastMessageTime = (messageTime: string, isEpoch = false) => {
  const messageTimeInDateTime = isEpoch ? dayjs(messageTime) : messageTime;

  const isToday = dayjs().isSame(messageTimeInDateTime, "day");
  const isYesterday = dayjs()
    .subtract(1, "day")
    .isSame(messageTimeInDateTime, "day");

  if (isToday) {
    return timeInAmPmFromDateTime(messageTimeInDateTime);
  } else if (isYesterday) {
    return "Yesterday";
  } else {
    return convertDateDDMMM(messageTimeInDateTime);
  }
};

const getLatestTimestampFunc = (lastMsgInternal: any, lastMsgPatient: any) => {
  const internalTimestamp = lastMsgInternal?.timestamp;
  const patientTimestamp = lastMsgPatient?.timestamp;

  // Case 1: Both timestamps exist, compare them to find the latest.
  if (internalTimestamp && patientTimestamp) {
    // This direct comparison works for Firebase Timestamp objects and numbers (milliseconds).
    return internalTimestamp > patientTimestamp
      ? internalTimestamp
      : patientTimestamp;
  }

  // Case 2: Only one of the timestamps exists, so it is the latest by default.
  if (internalTimestamp) {
    return internalTimestamp;
  }
  if (patientTimestamp) {
    return patientTimestamp;
  }

  // Case 3: Neither timestamp exists.
  return null;
};

const getMessageType: IKeyPairsValues = {
  [MESSAGE_TYPE.AUDIO]: "Audio",
  [MESSAGE_TYPE.ATTACHMENT]: "Attachment",
  [MESSAGE_TYPE.ORDER_MEDICINE]: "Order medicine",
  [MESSAGE_TYPE.CONSULTATION_END]: "Consultation end",
  [MESSAGE_TYPE.DISCHARGE]: "Discharged",
  [MESSAGE_TYPE.CANCELLED]: "Cancelled",
};

const chatMediaFilterList = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "Images",
    value: "images",
  },
  {
    label: "Documents",
    value: "documents",
  },
];
const inPatientFilterList = [
  {
    label: "All",
    value: IPDashboardFilterType.ALL,
    isSelection: false,
  },
  {
    label: "ICU",
    value: IPDashboardFilterType.ICU,
    isSelection: true,
  },
  {
    label: "Ward",
    value: IPDashboardFilterType.WARD,
    isSelection: true,
  },
  {
    label: "Hospital",
    value: IPDashboardFilterType.HOSPITAL,
    isSelection: true,
  },
  {
    label: "Active",
    value: IPDashboardFilterType.ACTIVE,
    isSelection: false,
  },
  {
    label: "Discharge",
    value: IPDashboardFilterType.DISCHARGE,
    isSelection: false,
  },
];
const chatListItem = [
  { type: ITabIndex.APPOINTMENTS },
  { type: ITabIndex.IN_PATIENTS },
];

const filterOutUndefined = (arr: any[]) => {
  return arr?.filter(Boolean);
};

const makeBillReducer = (
  state: MakeBillState,
  action: MakeBillAction
): MakeBillState => {
  switch (action.type) {
    case MakeBillActionType.SET_FEE:
      return { ...state, fee: action.payload };
    case MakeBillActionType.SET_BILL_SHARED_OPEN:
      return { ...state, isBillSharedOpen: action.payload };
    case MakeBillActionType.SET_BILL_OPEN_LOADING:
      return { ...state, isBillOpenLoading: action.payload };
    case MakeBillActionType.SET_BLOCK_UNBLOCK_CONFIRM:
      return { ...state, isBlockUnBlockConfirm: action.payload };
    case MakeBillActionType.SET_CHANGE_PAYMENT_METHOD_OPEN:
      return { ...state, isChangePaymentMethodOpen: action.payload };
    case MakeBillActionType.SET_PAYMENT_METHOD:
      return { ...state, paymentMethod: action.payload };
    case MakeBillActionType.SET_PAYMENT_LOADING:
      return { ...state, isPaymentConfirmLoading: action.payload };
    case MakeBillActionType.SET_PAYMENT_REFUND_LOADING:
      return { ...state, isPaymentRefundLoading: action.payload };
    case MakeBillActionType.SET_MAKE_REFUND_OPEN:
      return { ...state, isMakeRefundOpen: action.payload };
    case MakeBillActionType.SET_REFUND_AMOUNT:
      return { ...state, refundAmount: action.payload };
    case MakeBillActionType.SET_QR_CODE_OPEN:
      return { ...state, isQRCodeOpen: action.payload };
    default:
      return state;
  }
};

const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
  );
};

const isWKWebView = () => {
  return isIOS() && (window as any).webkit?.messageHandlers;
};

const checkBrowserCameraSupport = async () => {
  if (isWKWebView()) {
    // For WKWebView, we need to ensure we're on iOS 13+
    const iosVersion = parseInt(
      navigator.userAgent.match(/OS (\d+)_/)?.[1] || "0"
    );
    if (iosVersion < 13) {
      notify(
        INotifyEnum.ERROR,
        "Please upgrade to iOS 13 or later to use camera features"
      );
      return false;
    }
  }
  return true;
};

const checkCameraOrMicPermission = async () => {
  try {
    // First check browser support
    const browserSupported = await checkBrowserCameraSupport();
    if (!browserSupported) return false;

    // Then check actual permissions
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (err) {
    if (
      err.name === "NotAllowedError" ||
      err.name === "PermissionDeniedError"
    ) {
      notify(INotifyEnum.ERROR, "Camera or microphone permission was denied.");
      return false;
    } else if (
      err.name === "NotFoundError" ||
      err.name === "DevicesNotFoundError"
    ) {
      notify(
        INotifyEnum.ERROR,
        "No camera or microphone found on your device."
      );
      return false;
    } else if (
      err.name === "NotReadableError" ||
      err.name === "TrackStartError"
    ) {
      notify(
        INotifyEnum.ERROR,
        "Your camera or microphone is already in use by another application."
      );
      return false;
    } else {
      notify(
        INotifyEnum.ERROR,
        "Error accessing media devices. Please check your browser settings."
      );
      return false;
    }
  }
};

const filterInitialState: any = {
  [IDropDownSelectionType.TYPE_OF_CONSULTATION]: "",
  [IDropDownSelectionType.LANGUAGES]: "",
  [IDropDownSelectionType.SPECIALITY]: "",
  [IDropDownSelectionType.EXPERINCE]: "",
  [IDropDownSelectionType.DISTANCE]: "",
};

const clearLocalStorageExcept = (keyToKeep: ISessionKeys) => {
  const valueToKeep = storage.getSessionToken(keyToKeep);
  localStorage.clear();
  if (valueToKeep !== null) {
    storage.setSessionToken(keyToKeep, valueToKeep);
  }
};

const slotReserveFromList = [
  {
    label: "From Start",
    value: `${ISlotReserveFrom.SLOT_RESERVE_FROM_START.toString()}_From Start`,
  },
  {
    label: "From End",
    value: `${ISlotReserveFrom.SLOT_RESERVE_FROM_END.toString()}_From End`,
  },
];

const slotReserveValues = {
  "1": "From Start",
  "2": "From End",
};

const formatPhoneNumber = (number: string) => {
  const digits = (number || "").toString().replace(/\D/g, "");
  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
};

const registerServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) return null;

  try {
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
      {
        scope: "/",
      }
    );

    // Wait for service worker to be active
    await new Promise<void>((resolve) => {
      const checkActive = () => {
        if (registration.active) resolve();
        else setTimeout(checkActive, 50);
      };
      checkActive();
    });

    return registration;
  } catch (error) {
    console.error("Service Worker registration failed:", error);
    return null;
  }
};

const firstRouteMap = {
  about_us: "/about-us",
  our_app_features: "/app-feature",
  contact_us: "/contact-us",
  partner_with_us: "/partner-with-us",
  chat_with_us: "/chat",
  careers: "/contact-us",
  faqs: "/faq",
  blog_and_resources: "/health-hub",
  terms_and_conditions: "/terms-conditions",
  privacyPolicyText: "/privacy-policies",
};
const secondRouteMap = {
  grow_your_practice_online: "",
  video_consultation_tools: "/find-doctor",
  manage_appointments_and_ip_patients: "",
  access_medical_records_securely: "",
  doctor_diagnostic_clinics_pharmacies: "/contact-us",
  request_an_appointment: "/find-doctor",
  get_second_opinion: "/find-doctor",
};
const thirdRouteMap = {
  book_appointment: "/find-doctor",
  get_second_opinion: "/find-doctor",
  online_doctor_consultation: "/find-doctor",
  free_MRI_CT_review: "/chat",
  book_lab_tests: "/test-reports",
  order_medicines: "/medicines",
  neuro_health_tips: "/health-hub",
  neuro_health_assessment_tool: "/health-assessment?type=Brain",
};

const whoAreYouOptions = [
  { label: "Doctor", value: IWhoAreYouOptions.DOCTOR },
  { label: "Pharmacy", value: IWhoAreYouOptions.PHARMACY },
  { label: "Diagnostic Center", value: IWhoAreYouOptions.DIAGNOSTIC_CENTER },
  { label: "Clinic", value: IWhoAreYouOptions.CLINIC },
  { label: "Patient", value: IWhoAreYouOptions.PATIENT },
  { label: "Job Seeker", value: IWhoAreYouOptions.JOB_SEEKER },
  {
    label: "Other Healthcare Professional",
    value: IWhoAreYouOptions.OTHER_HEALTHCARE_PROFESSIONAL,
  },
];

const inquiryTypeOptions = [
  {
    label: IInquiryTypeOptions.PARTNERSHIP_OPPORTUNITIES,
    value: IInquiryTypeOptions.PARTNERSHIP_OPPORTUNITIES,
  },
  { label: "Appointment Help", value: IInquiryTypeOptions.APPOINTMENT_HELP },
  { label: "Support Request", value: IInquiryTypeOptions.SUPPORT_REQUEST },
  { label: "Job Application", value: IInquiryTypeOptions.JOB_APPLICATION },
  { label: "Feedback", value: IInquiryTypeOptions.FEEDBACK },
  { label: "Other", value: IInquiryTypeOptions.OTHER },
];

const contactUsFields = [
  {
    name: "name",
    label: "Name *",
    placeholder: "Name *",
    InputProps: { inputMode: "text" },
  },
  {
    name: "mobileNumber",
    label: "Mobile Number *",
    placeholder: "Mobile Number *",
    InputProps: {
      inputMode: "numeric",
      maxLength: 10,
      onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
      },
    },
  },
  {
    name: "emailId",
    label: "Email ID",
    placeholder: "Email ID",
    InputProps: { inputMode: "text" },
  },
  {
    name: "organizationName",
    label: "Organization Name",
    placeholder: "Organization Name",
    InputProps: { inputMode: "text" },
  },
];

const consultationCards = (data: PatientsSummaryData) => {
  return [
    {
      label: "Total Consultation",
      value: IConsultationStatus.TOTAL,
      count: data?.total?.count || 0,
    },
    {
      label: "Completed Consultation",
      value: IConsultationStatus.COMPLETED,
      count: data?.completed?.count || 0,
    },
    {
      label: "Upcoming Consultation",
      value: IConsultationStatus.UPCOMING,
      count: data?.upcoming?.count || 0,
    },
    {
      label: "Canceled Consultation",
      value: IConsultationStatus.CANCELED,
      count: data?.cancelled?.count || 0,
    },
  ];
};
const selectMonthChatList = [
  { label: "Last 12 Months", value: ITimePeriod.LAST_12_MONTHS },
  { label: "Last 6 Months", value: ITimePeriod.LAST_6_MONTHS },
  { label: "Next 45 Days", value: ITimePeriod.NEXT_45_DAYS },
];

const patientSummaryOrIPAnalyticsInitialState: IPatientSummaryOrIpAnalyticsState =
  {
    dateRange: {
      startDate: dayjs()
        .subtract(1, "year")
        .endOf("month")
        .add(1, "day")
        .format("YYYY-MM-DD"),
      endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
      selectedValue: ITimePeriod.LAST_12_MONTHS,
    },
    bookedBy: null,
    isExporting: false,
    selectedStat: IConsultationStatus.TOTAL,
    selectedHospital: null,
  };

const patientSummaryOrIpAnalyticsReducer = (
  state: IPatientSummaryOrIpAnalyticsState,
  action: IPatientSummaryOrIpAnalyticsAction
): IPatientSummaryOrIpAnalyticsState => {
  switch (action.type) {
    case IPatientSummaryOrIpAnalyticsActionType.SET_DATE_RANGE:
      return {
        ...state,
        dateRange: {
          ...state.dateRange,
          ...action.payload,
        },
      };
    case IPatientSummaryOrIpAnalyticsActionType.SET_BOOKED_BY:
      return { ...state, bookedBy: action.payload };
    case IPatientSummaryOrIpAnalyticsActionType.SET_EXPORTING:
      return { ...state, isExporting: action.payload };
    case IPatientSummaryOrIpAnalyticsActionType.SET_SELECTED_STAT:
      return { ...state, selectedStat: action.payload };
    case IPatientSummaryOrIpAnalyticsActionType.SET_SELECTED_HOSPITAL:
      return {
        ...state,
        selectedHospital: action.payload,
      };
    default:
      return state;
  }
};

const getLatestUnreadOrRecentLastMessage = (data) => {
  const internalChat = data?.internal;
  const internalChatSeen = internalChat?.seen;
  const patientChat = data?.patient;
  const patientChatSeen = patientChat?.seen;
  const userId = storage.getSessionToken(ISessionKeys.USER_ID);

  const internalHasUnread =
    internalChatSeen && internalChatSeen[userId]?.unReadMessageCount > 0;
  const patientHasUnread =
    patientChatSeen && patientChatSeen[userId]?.unReadMessageCount > 0;
  // Unread priority
  if (internalHasUnread && !patientHasUnread) {
    return internalChat;
  }
  if (patientHasUnread && !internalHasUnread) {
    return patientChat;
  }

  // If both have unread or both read, compare timestamps
  const internalTime = internalChat?.timestamp
    ? new Date(internalChat.timestamp)
    : null;
  const patientTime = patientChat?.timestamp
    ? new Date(patientChat.timestamp)
    : null;

  // Only one has timestamp
  if (internalTime && !patientTime) {
    return internalChat;
  }
  if (patientTime && !internalTime) {
    return patientChat;
  }
  // Both have timestamp
  if (internalTime && patientTime) {
    return internalTime > patientTime ? internalChat : patientChat;
  }
  // Neither has timestamp: return null or preferred fallback (example: internalChat)
  return null;
};

const hasEveryoneRead = (message: Record<string, any>) => {
  if (!message || !message.seen) {
    return false;
  }
  return Object.values(message.seen).every((status) => status === true);
};

const hasStaffOrDoctorRead = (
  message: Record<string, any>,
  staffMembers: string[]
) => {
  if (!message || !message.seen) {
    return false;
  }
  const seen = {};
  staffMembers?.forEach((member) => {
    seen[member] = message?.seen[member] || false;
  });
  return Object.values(seen).every((status) => status === true);
};

export {
  hasEveryoneRead,
  hasStaffOrDoctorRead,
  getLatestUnreadOrRecentLastMessage,
  contactUsFields,
  patientSummaryOrIPAnalyticsInitialState,
  patientSummaryOrIpAnalyticsReducer,
  selectMonthChatList,
  consultationCards,
  whoAreYouOptions,
  inquiryTypeOptions,
  firstRouteMap,
  secondRouteMap,
  thirdRouteMap,
  registerServiceWorker,
  formatPhoneNumber,
  slotReserveValues,
  calculateSlotDuration,
  clearLocalStorageExcept,
  filterInitialState,
  checkCameraOrMicPermission,
  makeBillReducer,
  filterOutUndefined,
  chatMediaFilterList,
  getMessageType,
  getLastMessageTime,
  tranformToDays,
  generateDeviceId,
  clinicAppointmentSegregation,
  getAppointmentSlotDetail,
  truncateFileName,
  getFileNameFromUrl,
  getTimeDifferenceInMinutes,
  filteredNullEmptyArrayData,
  convertTimestampToISO,
  getMedicalReportDocumentName,
  convertTimestampToDDMMM,
  defaultCalendarDatesTimeStamp,
  diagnosticOrderFilterList,
  convertDateToTimeStamp,
  convertKeysToCamelCase,
  isClinic,
  getPermissionAccess,
  getLocation,
  convertDDMMYYYYHHMM,
  getHealthAssessmentImageByType,
  randomCardColorGenerator,
  timeInAmPmFromDateTime,
  universalSearchType,
  doctorChipSearchList,
  getGreeting,
  removeAttributesAdmitPatientDetail,
  getDay,
  admitPatientinitialState,
  admitPatientDispatcher,
  getTodayTomorrowDate,
  ipDashboardListingWeb,
  ipDashboardListing,
  transformDateToDDMMYYYY,
  calculateDayFromTargetDate,
  languagesListInfo,
  consultationTypes,
  orderTypes,
  scheduleFilterList,
  downloadReport,
  convertDateDDMMM,
  convertDateYYYYMMDD,
  detectOS,
  base64ToFile,
  transformDateToMMDDYYY,
  openClinicMapLocation,
  findDoctorFilterListing,
  getNextDates,
  getDateTime,
  languagesList,
  OrderTypeFilterList,
  transformClasses,
  tranformIndexesToDays,
  convertDaysToRanges,
  timeInAmPmFormat,
  tranformDaysToIndexes,
  doctorSpeakLanguages,
  doctorDegreeName,
  topBarDropdownDispatchingEnums,
  pharmacyOrderFilterList,
  capitalizeFirstLetter,
  roundToTwoDecimals,
  getDateFormat,
  formatTimestamp,
  defaultCalendarDates,
  patientChipSearchList,
  diagnosticChipSearchList,
  DIAGNOSTIC_TAB,
  pdfRegex,
  IDELIVERY_TYPE,
  LIMIT,
  receptionFilterList,
  inPatientFilterList,
  chatListItem,
  PAGE_LIMIT,
  reportsFilterList,
  dayFilterListing,
  slotReserveFromList,
  getLatestTimestampFunc,
};
